"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { getNavigationItems } from "~/lib/navigation-config";
import { getPermissionLabel } from "~/lib/permissions";
import { logoutAction } from "~/server/actions/auth-actions";
import { EXTERNAL_URLS } from "~/lib/external-urls";
import {
	useEffectiveUser,
	useImpersonation,
} from "~/lib/impersonation-context";
import { useToast } from "~/components/ui/toast";
import {
	DashboardIcon,
	BuildingIcon,
	CompassIcon,
	UsersIcon,
	ZapIcon,
	MessageCircleIcon,
	SearchIcon,
	PhoneIcon,
	LogOutIcon,
	UserIcon,
	XIcon,
	ArelloLogo,
} from "~/components/icons";

interface NavigationProps {
	className?: string;
	isMobileMenuOpen?: boolean;
	onMobileMenuToggle?: (isOpen: boolean) => void;
}

export default function Navigation({
	className = "",
	isMobileMenuOpen = false,
	onMobileMenuToggle,
}: NavigationProps) {
	const { effectiveUser, isLoading } = useEffectiveUser();
	const { endImpersonation } = useImpersonation();
	const { showToast } = useToast();
	const pathname = usePathname();
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
	const [isLoggingOut, setIsLoggingOut] = useState(false);

	const handleDownload = useCallback(
		(href: string, filename: string) => {
			const link = document.createElement("a");
			link.href = href;
			link.download = filename;
			link.style.display = "none";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			showToast("Download started", "success", {
				description: "The file is being downloaded.",
			});
		},
		[showToast],
	);

	useEffect(() => {
		if (!effectiveUser) return;

		const permission = effectiveUser.permission;
		const navItems = getNavigationItems(permission);

		const itemsToExpand: string[] = [];

		navItems.forEach((item) => {
			if (item.hasSubmenu && item.subItems) {
				const hasActiveChild = item.subItems.some(
					(subItem) =>
						pathname === subItem.href ||
						pathname.startsWith(subItem.href + "/"),
				);

				const isInParentSection =
					pathname.startsWith(item.href) && item.href !== "/";

				if (hasActiveChild || isInParentSection) {
					itemsToExpand.push(item.id);
				}
			}
		});

		if (itemsToExpand.length > 0) {
			setExpandedItems((prev) => {
				const hasNewItems = itemsToExpand.some((id) => !prev.has(id));
				if (!hasNewItems) return prev;

				const merged = new Set(prev);
				itemsToExpand.forEach((id) => merged.add(id));
				return merged;
			});
		}
	}, [pathname, effectiveUser?.permission]);

	useEffect(() => {
		if (onMobileMenuToggle) {
			onMobileMenuToggle(false);
		}
	}, [pathname, onMobileMenuToggle]);

	useEffect(() => {
		if (isMobileMenuOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [isMobileMenuOpen]);

	if (isLoading && !effectiveUser) {
		return <NavigationSkeleton className={className} />;
	}

	if (!effectiveUser) {
		return null;
	}

	const permission = effectiveUser.permission;
	const navItems = getNavigationItems(permission);
	const permissionLabel = getPermissionLabel(permission);

	const toggleExpanded = (itemId: string) => {
		setExpandedItems((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(itemId)) {
				newSet.delete(itemId);
			} else {
				newSet.add(itemId);
			}
			return newSet;
		});
	};

	const handleLogout = async () => {
		setIsLoggingOut(true);
		endImpersonation();
		await logoutAction();
	};

	const isActive = (href: string) => {
		if (href === "/") {
			return pathname === "/";
		}
		return pathname.startsWith(href);
	};

	const handleCloseMobileMenu = () => {
		if (onMobileMenuToggle) {
			onMobileMenuToggle(false);
		}
	};

	const NavigationContent = ({
		isMobile = false,
	}: {
		isMobile?: boolean;
	}) => (
		<>
			{/* Main Menu */}
			<div className="flex flex-1 flex-col gap-2 overflow-y-auto">
				{/* Section Title (for Admin only) */}
				{permission === "a" && (
					<div className="flex items-center px-3">
						<p className="h-4 flex-1 text-xs font-bold leading-4 tracking-[0.04px] text-[#8b8d98]">
							ADMINISTRATOR
						</p>
					</div>
				)}

				{/* Navigation Items */}
				{navItems.map((item) => {
					const active = isActive(item.href);
					const expanded = expandedItems.has(item.id);
					const hasSubItems =
						item.hasSubmenu &&
						item.subItems &&
						item.subItems.length > 0;

					return (
						<div
							key={item.id}
							className="flex w-full flex-col gap-1"
						>
							{hasSubItems ? (
								<>
									<button
										onClick={() => toggleExpanded(item.id)}
										className={`
                      flex w-full items-center gap-3 rounded py-3 px-3 cursor-pointer
                      ${expanded ? "bg-[#f0f0f3]" : "hover:bg-[#ececf1]"}
                    `}
									>
										<div className="flex flex-1 items-center gap-3">
											<NavigationIcon
												name={item.icon}
												isActive={false}
											/>
											<p className="flex-1 text-sm leading-5 text-left font-normal text-[#1c2024]">
												{item.label}
											</p>
										</div>
										<ChevronIcon expanded={expanded} />
									</button>
									{expanded && item.subItems && (
										<div className="flex flex-col">
											{item.subItems.map(
												(subItem, index) => {
													const isLast =
														index ===
														item.subItems!.length -
														1;
													const subActive =
														!subItem.isExternal &&
														!subItem.isDownload &&
														(pathname ===
															subItem.href ||
															pathname.startsWith(
																subItem.href +
																"/",
															));

													if (subItem.isDownload) {
														const filename =
															subItem.href
																.split("/")
																.pop() ??
															"download";
														return (
															<button
																key={subItem.id}
																onClick={() => {
																	handleDownload(
																		subItem.href,
																		filename,
																	);
																	if (
																		isMobile
																	)
																		handleCloseMobileMenu();
																}}
																className="flex items-center text-left"
															>
																{/* Tree node connector */}
																<div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
																	<div
																		className={`
                                  absolute left-5 w-px bg-[#d9d9e0]
                                  ${index === 0 ? "top-0 h-full" : isLast ? "top-0 h-1/2" : "top-0 h-full"}
                                `}
																	/>
																	<div className="absolute left-5 top-1/2 h-px w-3 bg-[#d9d9e0]" />
																	<div className="absolute left-[30px] z-10 h-1.5 w-1.5 rounded-full bg-[#d9d9e0]" />
																</div>
																<div className="flex flex-1 items-left gap-2 py-2 px-3 rounded hover:bg-[#ececf1]">
																	<p className="text-sm leading-5 font-normal text-[#1c2024]">
																		{
																			subItem.label
																		}
																	</p>
																	{subItem.icon && (
																		<subItem.icon className="h-4 w-4 text-[#8b8d98] shrink-0 ml-auto" />
																	)}
																</div>
															</button>
														);
													}

													const LinkComponent =
														subItem.isExternal
															? "a"
															: Link;
													const linkProps =
														subItem.isExternal
															? {
																href: subItem.href,
																target: "_blank",
																rel: "noopener noreferrer",
																onClick:
																	isMobile
																		? handleCloseMobileMenu
																		: undefined,
															}
															: {
																href: subItem.href,
																onClick:
																	isMobile
																		? handleCloseMobileMenu
																		: undefined,
															};

													return (
														<LinkComponent
															key={subItem.id}
															{...linkProps}
															className="flex items-center"
														>
															{/* Tree node connector */}
															<div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
																{/* Vertical line */}
																<div
																	className={`
                                absolute left-5 w-px bg-[#d9d9e0]
                                ${index === 0 ? "top-0 h-full" : isLast ? "top-0 h-1/2" : "top-0 h-full"}
                              `}
																/>
																{/* Horizontal connector line */}
																<div className="absolute left-5 top-1/2 h-px w-3 bg-[#d9d9e0]" />
																{/* Node dot */}
																<div
																	className={`
                                absolute left-[30px] z-10 h-1.5 w-1.5 rounded-full
                                ${subActive ? "bg-[#4c6f90]" : "bg-[#d9d9e0]"}
                              `}
																/>
															</div>
															{/* Sub-item label */}
															<div
																className={`
                              flex flex-1 items-center gap-2 py-2 px-3 rounded
                              ${subActive ? "bg-[#4c6f90]" : "hover:bg-[#ececf1]"}
                            `}
															>
																<p
																	className={`
                                text-sm leading-5
                                ${subActive ? "font-medium text-white" : "font-normal text-[#1c2024]"}
                              `}
																>
																	{
																		subItem.label
																	}
																</p>
																{subItem.icon && (
																	<subItem.icon
																		className={`h-4 w-4 shrink-0 ml-auto ${subActive ? "text-white" : "text-[#8b8d98]"}`}
																	/>
																)}
															</div>
														</LinkComponent>
													);
												},
											)}
										</div>
									)}
								</>
							) : (
								<Link
									href={item.href}
									onClick={
										isMobile
											? handleCloseMobileMenu
											: undefined
									}
									className={`
                    flex w-full items-center gap-3 rounded py-3 px-3
                    ${active ? "bg-[#4c6f90] text-white" : "text-[#1c2024] hover:bg-[#ececf1]"}
                  `}
								>
									<NavigationIcon
										name={item.icon}
										isActive={active}
									/>
									<p
										className={`
                      flex-1 text-sm leading-5 text-left
                      ${active ? "font-medium text-white" : "font-normal text-[#1c2024]"}
                    `}
									>
										{item.label}
									</p>
								</Link>
							)}
						</div>
					);
				})}
			</div>

			{/* Separator */}
			<div className="flex w-full items-center overflow-hidden">
				<div className="h-px flex-1 bg-[rgba(20,0,53,0.15)]" />
			</div>

			{/* User Section */}
			<div className="flex w-full flex-col gap-2">
				{/* Contact Us */}
				<a
					href={EXTERNAL_URLS.ARELLO.ABOUT.CONTACT}
					target="_blank"
					rel="noopener noreferrer"
					onClick={isMobile ? handleCloseMobileMenu : undefined}
					className="flex w-full items-center gap-3 rounded py-3 px-3 text-[#1c2024] hover:bg-[#ececf1]"
				>
					<ContactIcon />
					<p className="flex-1 text-sm font-normal leading-5 text-left">
						Contact Us
					</p>
				</a>

				{/* Log Out */}
				<button
					onClick={handleLogout}
					disabled={isLoggingOut}
					className="flex w-full items-center gap-3 rounded py-3 px-3 text-[#1c2024] hover:bg-[#ececf1] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
				>
					<LogOutIconComponent />
					<p className="flex-1 text-sm font-normal leading-5 text-left">
						{isLoggingOut ? "Logging out..." : "Log Out"}
					</p>
				</button>

				{/* User Avatar & Info - Clickable to go to settings */}
				<Link
					href="/settings"
					onClick={isMobile ? handleCloseMobileMenu : undefined}
					className="flex h-14 w-full items-center gap-2 rounded py-2 px-3 hover:bg-[#ececf1] transition-colors"
				>
					<div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[rgba(0,116,221,0.09)]">
						<UserIconComponent />
					</div>
					<div className="flex min-w-0 flex-col">
						<p className="truncate text-sm font-semibold leading-5 text-[#1c2024]">
							{effectiveUser.username}
						</p>
						<p className="truncate text-xs leading-4 text-[#60646c]">
							{permissionLabel}
						</p>
					</div>
				</Link>
			</div>

			{/* Footer */}
			<div className="flex items-center justify-center gap-2">
				<p className="text-xs leading-4 text-[#60646c]">
					© 2025 ARELLO
				</p>
				<div className="relative h-4 w-0">
					<div className="absolute bottom-0 left-[-0.25px] right-[-0.25px] top-0 border-l border-[rgba(0,8,48,1)]" />
				</div>
				<Link
					href="/general-information/terms"
					onClick={isMobile ? handleCloseMobileMenu : undefined}
					className="text-xs leading-4 text-[rgba(0,50,97,0.7)] hover:text-[rgba(0,50,97,0.9)]"
				>
					Terms of Use
				</Link>
			</div>
		</>
	);

	return (
		<>
			{/* Desktop Navigation - Hidden on mobile */}
			<nav
				className={`
          hidden lg:flex h-screen w-[265px] flex-col gap-8 border-r border-[#d9d9e0] bg-[#f9f9fb]
          pl-4 pr-4 py-6 ${className}
        `}
			>
				{/* Logo */}
				<div className="flex w-full items-center gap-[10.267px]">
					<div className="relative h-8 w-[117px]">
						<ArelloLogo variant="dark" className="h-full w-full" />
					</div>
					<div className="flex h-[47.912px] items-center overflow-hidden bg-transparent">
						<div className="h-full w-[0.856px] bg-[rgba(0,0,45,0.09)]" />
					</div>
					<div className="flex min-w-0 flex-1 flex-col justify-center text-xs leading-[16px] tracking-[0.04px] text-[#1c2024]">
						<p className="whitespace-pre-wrap">
							Disciplinary Action Database
						</p>
					</div>
				</div>

				<NavigationContent isMobile={false} />
			</nav>

			{/* Mobile Navigation Overlay - Visible only when menu is open */}
			{isMobileMenuOpen && (
				<div className="lg:hidden fixed inset-0 z-50 flex flex-col bg-[#f9f9fb]">
					{/* Mobile Header */}
					<div className="flex h-[72px] items-center justify-between border-b border-[#d9d9e0] px-4">
						<div className="flex items-center gap-2">
							<div className="relative h-[26px] w-[97.5px]">
								<ArelloLogo
									variant="dark"
									className="h-full w-full"
								/>
							</div>
							<div className="flex h-10 items-center">
								<div className="h-full w-px bg-[rgba(0,0,45,0.09)]" />
							</div>
							<span className="text-xs leading-4 text-[#1c2024]">
								Disciplinary Action Database
							</span>
						</div>
						<button
							onClick={handleCloseMobileMenu}
							className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
							aria-label="Close menu"
						>
							<XIcon className="h-[18px] w-[18px] text-[#003261]" />
						</button>
					</div>

					{/* Mobile Menu Content */}
					<div className="flex flex-1 flex-col gap-8 overflow-y-auto px-4 py-6">
						<NavigationContent isMobile={true} />
					</div>
				</div>
			)}
		</>
	);
}

// Mobile Header Component (for use in LayoutContent)
export function MobileHeader({ onMenuToggle }: { onMenuToggle: () => void }) {
	return (
		<div className="lg:hidden flex h-[72px] items-center justify-between border-b border-[#d9d9e0] bg-[#f9f9fb] px-4">
			<div className="flex items-center gap-2">
				<div className="relative h-[26px] w-[97.5px]">
					<ArelloLogo variant="dark" className="h-full w-full" />
				</div>
				<div className="flex h-10 items-center">
					<div className="h-full w-px bg-[rgba(0,0,45,0.09)]" />
				</div>
				<span className="text-xs leading-4 text-[#1c2024]">
					Disciplinary Action Database
				</span>
			</div>
			<button
				onClick={onMenuToggle}
				className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-gray-100 transition-colors"
				aria-label="Open menu"
			>
				<MenuIcon className="h-[18px] w-[18px] text-[#003261]" />
			</button>
		</div>
	);
}

// Icon Components
function NavigationIcon({
	name,
	isActive,
}: {
	name: string;
	isActive: boolean;
}) {
	const color = isActive ? "#ffffff" : "#8b8d98";
	const className = "h-5 w-5";
	const style: React.CSSProperties = { color };

	switch (name) {
		case "dashboard":
			return <DashboardIcon className={className} style={style} />;
		case "building":
			return <BuildingIcon className={className} style={style} />;
		case "compass":
			return <CompassIcon className={className} style={style} />;
		case "users":
			return <UsersIcon className={className} style={style} />;
		case "zap":
			return <ZapIcon className={className} style={style} />;
		case "message-circle":
			return <MessageCircleIcon className={className} style={style} />;
		case "search":
			return <SearchIcon className={className} style={style} />;
		case "edit":
			return (
				<svg className={className} viewBox="0 0 20 20" fill="none">
					<path
						d="M14.1667 2.5C14.3856 2.28113 14.6454 2.10752 14.9314 1.98906C15.2173 1.87061 15.5238 1.80965 15.8333 1.80965C16.1429 1.80965 16.4493 1.87061 16.7353 1.98906C17.0213 2.10752 17.281 2.28113 17.5 2.5C17.7189 2.71887 17.8925 2.97863 18.0109 3.26459C18.1294 3.55055 18.1903 3.85702 18.1903 4.16667C18.1903 4.47631 18.1294 4.78278 18.0109 5.06874C17.8925 5.3547 17.7189 5.61446 17.5 5.83333L6.25 17.0833L1.66667 18.3333L2.91667 13.75L14.1667 2.5Z"
						stroke={color}
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			);
		case "user":
			return (
				<svg className={className} viewBox="0 0 20 20" fill="none">
					<path
						d="M16.6667 17.5V15.8333C16.6667 14.9493 16.3155 14.1014 15.6904 13.4763C15.0652 12.8512 14.2174 12.5 13.3333 12.5H6.66667C5.78261 12.5 4.93476 12.8512 4.30964 13.4763C3.68452 14.1014 3.33333 14.9493 3.33333 15.8333V17.5M13.3333 6.16667C13.3333 8.00762 11.841 9.5 10 9.5C8.15905 9.5 6.66667 8.00762 6.66667 6.16667C6.66667 4.32572 8.15905 2.83333 10 2.83333C11.841 2.83333 13.3333 4.32572 13.3333 6.16667Z"
						stroke={color}
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			);
		case "info-circle":
			return (
				<svg className={className} viewBox="0 0 20 20" fill="none">
					<path
						d="M10 13.3333V10M10 6.66667H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
						stroke={color}
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			);
		default:
			return null;
	}
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
	return (
		<svg
			className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
			viewBox="0 0 16 16"
			fill="none"
		>
			<path
				d="M4 6L8 10L12 6"
				stroke="#60646c"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function MenuIcon({ className }: { className?: string }) {
	return (
		<svg className={className} viewBox="0 0 18 18" fill="none">
			<path
				d="M2.25 4.5H15.75M2.25 9H15.75M2.25 13.5H15.75"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function ContactIcon() {
	return <PhoneIcon className="h-5 w-5" style={{ color: "#8b8d98" }} />;
}

function LogOutIconComponent() {
	return <LogOutIcon className="h-5 w-5" style={{ color: "#8b8d98" }} />;
}

function UserIconComponent() {
	return <UserIcon className="h-4 w-4" style={{ color: "#003261" }} />;
}

// Loading skeleton for navigation
function NavigationSkeleton({ className = "" }: { className?: string }) {
	return (
		<nav
			className={`
        hidden lg:flex h-screen w-[265px] flex-col gap-8 border-r border-[#d9d9e0] bg-[#f9f9fb]
        pl-4 pr-4 py-6 ${className}
      `}
		>
			{/* Logo Skeleton */}
			<div className="flex w-full items-center gap-[10.267px]">
				<div className="h-8 w-[117px] bg-gray-200 animate-pulse rounded" />
				<div className="flex h-[47.912px] items-center overflow-hidden bg-transparent">
					<div className="h-full w-[0.856px] bg-[rgba(0,0,45,0.09)]" />
				</div>
				<div className="flex-1 h-8 bg-gray-200 animate-pulse rounded" />
			</div>

			{/* Menu Items Skeleton */}
			<div className="flex flex-1 flex-col gap-2">
				{[1, 2, 3, 4, 5].map((i) => (
					<div
						key={i}
						className="h-12 bg-gray-200 animate-pulse rounded"
					/>
				))}
			</div>

			{/* Separator */}
			<div className="flex w-full items-center overflow-hidden">
				<div className="h-px flex-1 bg-[rgba(20,0,53,0.15)]" />
			</div>

			{/* User Section Skeleton */}
			<div className="flex w-full flex-col gap-2">
				<div className="h-12 bg-gray-200 animate-pulse rounded" />
				<div className="h-12 bg-gray-200 animate-pulse rounded" />
				<div className="h-14 bg-gray-200 animate-pulse rounded" />
			</div>
		</nav>
	);
}
